import moment from 'moment';

import { INVALID_DATE, YEAR_MONTH_FIRST_DAY_FORMAT, isValidInteger } from './inputHelpers';

const IHI_REGEX_PATTERN = '^800360\\d{10}$';
const HPI_I_REGEX_PATTERN = '^800361\\d{10}$';
const HPI_O_REGEX_PATTERN = '^800362\\d{10}$';
const PRESCRIBER_NUMBER_REGEX_PATTERN = '^\\d{7}$';

/**
 * Validates whether the given expiry date is either invalid or in the past.
 *
 * A date is considered invalid if:
 * - It cannot be formatted to the expected format, or
 * - It is a valid date but occurs before the current date.
 *
 * @param {*} value - The date value to validate.
 * @returns {boolean} Returns true if the date is invalid or in the past, otherwise false.
 */
export const isInvalidateExpiryDate = (value) =>
  moment(value).format(YEAR_MONTH_FIRST_DAY_FORMAT) === INVALID_DATE || moment(value).isBefore(moment());

/**
 * Validates if the input matches the given regex pattern.
 * @param {object} param - The parameter object.
 * @param {string} param.input - The input string to validate.
 * @param {string} param.pattern - The regex pattern to match.
 * @returns {boolean} True if input matches the pattern, otherwise false.
 */
const validatePattern = ({ input, pattern }) => {
  if (typeof input !== 'string' || typeof pattern !== 'string') {
    return false;
  }

  const regex = new RegExp(pattern);
  return regex.test(input);
};

/**
 * Performs the Luhn check to validate a 16-digit number.
 * The Luhn algorithm is used to validate various identification numbers.
 * Reference: https://stripe.com/au/resources/more/how-to-use-the-luhn-algorithm-a-guide-in-applications-for-businesses
 *
 * @param {string} valueToValidate - The 16-digit number string to validate.
 * @returns {boolean} True if the number is valid according to the Luhn algorithm, otherwise false.
 */
const luhnCheck = (valueToValidate) => {
  if (typeof valueToValidate !== 'string' || !/^\d{16}$/.test(valueToValidate)) {
    return false;
  }

  const digits = valueToValidate.split('').reverse().map(Number);
  const [lastDigit, ...rest] = digits;

  const sum = rest.reduce((acc, val, i) => {
    const double = i % 2 === 0 ? val * 2 : val;
    return acc + (double > 9 ? double - 9 : double);
  }, 0);

  return (sum + lastDigit) % 10 === 0;
};

/**
 * Validates an Individual Healthcare Identifier which identifies (IHI) number.
 * IHI: Identifies a patient (individual) receiving healthcare.
 * It is a 16-digit number which starts with 800360
 * @param {object} param - The parameter object.
 * @param {string} param.input - The IHI number to validate.
 * @returns {boolean} True if valid, otherwise false.
 */
export const validateIHINumber = ({ input }) => {
  if (typeof input !== 'string') {
    return false;
  }

  return validatePattern({ input, pattern: IHI_REGEX_PATTERN }) && luhnCheck(input);
};

/**
 * Validates a Healthcare Provider Identifier-Organisation (HPI-O) number.
 * HPI-O: identifies the healthcare provider organisation where healthcare is provided.
 * Is given to organisations like hospitals, health or GP clinics, pathology, diagnostics centres, etc.
 * It is a 16-digit number which starts with 800362
 * @param {object} param - The parameter object.
 * @param {string} param.input - The HPI-O number to validate.
 * @returns {boolean} True if valid, otherwise false.
 */
export const validateHPIONumber = ({ input }) => {
  if (typeof input !== 'string') {
    return false;
  }
  return validatePattern({ input, pattern: HPI_O_REGEX_PATTERN }) && luhnCheck(input);
};

/**
 * Validates a PBS Prescriber Number (PBS).
 * PBS prescriber number: uniquely identifies that an individual healthcare provider is eligible to prescribe medication under the PBS.
 * Isn’t location specific
 * It is a 7-digit number
 * @param {object} param - The parameter object.
 * @param {string} param.input - The PBS prescriber number to validate.
 * @returns {boolean} True if valid, otherwise false.
 */
export const validatePBSNumber = ({ input }) => {
  if (typeof input !== 'string') {
    return false;
  }
  return validatePattern({ input, pattern: PRESCRIBER_NUMBER_REGEX_PATTERN });
};


/**
 * Validates a Healthcare Provider Identifier-Individual (HPI-I) number.
 * HPI-I: identifies an individual healthcare provider who provides healthcare.
 * This number is allocated by Medicare and is different from the national registration number issued by AHPRA
 * It is a 16-digit number which starts with 800361
 * @param {object} param - The parameter object.
 * @param {string} param.input - The HPI-I number to validate.
 * @returns {boolean} True if valid, otherwise false.
 */
export const validateHPIINumber = ({ input }) => {
  if (typeof input !== 'string') {
    return false;
  }

  return validatePattern({ input, pattern: HPI_I_REGEX_PATTERN }) && luhnCheck(input);
};

/**
 * Applicable for both patient's family name and given name.
 * @description
 * Reference: https://developer.digitalhealth.gov.au/resources/download/component/7863/c7785e47-d9c1-4687-a590-3555c27bba25
 * Sheet: UC.035
 * Test Case ID: HI_035_005805
 */
export const CONCESSION_MAXIMUM_NAME_LENGTH = 40;
export const MEDICARE_IRN_LENGTH = 1;
export const MEDICARE_NUMBER_LENGTH = 10;

const MEDICARE_FIRST_DIGIT_MIN = 2;
const MEDICARE_FIRST_DIGIT_MAX = 6;
const MEDICARE_CHECK_MULTIPLIERS = {
  FIRST_DIGIT: 1,
  SECOND_DIGIT: 3,
  THIRD_DIGIT: 7,
  FOURTH_DIGIT: 9,
  FIFTH_DIGIT: 1,
  SIXTH_DIGIT: 3,
  SEVENTH_DIGIT: 7,
  EIGHTH_DIGIT: 9,
};

/**
 * Validates an Australian Medicare number.
 * Reference: https://developer.digitalhealth.gov.au/namespaces/id/medicare-number/index.html
 *
 * @param {string} value - The Medicare number to validate.
 * @returns {boolean} `true` if the Medicare number is valid, otherwise `false`.
 *
 * @description
 * Medicare card number format
 * The Medicare card number comprises:
 * • Eight digits;
 * • A check digit (one digit); and
 * • An issue number (one digit).
 * Note: the first digit of the Medicare card number should be in the range 2 to 6.
 * 
 * Medicare card number check digit calculation
 * 1. Calculate the sum of: ((digit 1) + (digit 2 * 3) + (digit 3 * 7) + (digit 4 * 9) + (digit 5) + (digit 6 * 3) + (digit 7 * 7) + (digit 8 * 9)),
 * where digit 1 is the highest place value digit of the Medicare card number and digit 8 is the lowest place value digit of the Medicare card number.
 * Example: for Medicare card number '2123 45670 1', digit 1 is 2 and digit 8 is 7.
 * 2. Divide the calculated sum by 10.
 * 3. The check digit is the remainder.
 * 
 * Example: For Medicare card number 2123 4567.
 * 1. (2) + (1 * 3) + (2 * 7) + (3 * 9) + (4) + (5 * 3) + (6 * 7) + (7 * 9) = 170
 * 2. Divide 170 by 10. The remainder is 0.
 * 3. The check digit for this Medicare number is 0.
 * Source: https://developer.digitalhealth.gov.au/resources/download/component/7863/c7785e47-d9c1-4687-a590-3555c27bba25
 * Sheet: Medicare Card No. Check Algrthm
 */
export const validateMedicareNumber = (value) => {
  if (typeof value !== 'string') {
    return false;
  }

  if (value.length !== MEDICARE_NUMBER_LENGTH) {
    return false;
  }

  if (!isValidInteger(value)) {
    return false;
  }

  const [first, second, third, fourth, fifth, sixth, seventh, eighth, checkDigitValidator] = value
    .split('')
    .map(Number);

  if (MEDICARE_FIRST_DIGIT_MIN > first || first > MEDICARE_FIRST_DIGIT_MAX) {
    return false;
  }

  const sum =
    first * MEDICARE_CHECK_MULTIPLIERS.FIRST_DIGIT +
    second * MEDICARE_CHECK_MULTIPLIERS.SECOND_DIGIT +
    third * MEDICARE_CHECK_MULTIPLIERS.THIRD_DIGIT +
    fourth * MEDICARE_CHECK_MULTIPLIERS.FOURTH_DIGIT +
    fifth * MEDICARE_CHECK_MULTIPLIERS.FIFTH_DIGIT +
    sixth * MEDICARE_CHECK_MULTIPLIERS.SIXTH_DIGIT +
    seventh * MEDICARE_CHECK_MULTIPLIERS.SEVENTH_DIGIT +
    eighth * MEDICARE_CHECK_MULTIPLIERS.EIGHTH_DIGIT;

  const checkDigit = sum % 10;

  return checkDigit === checkDigitValidator;
};

export const DVA_NUMBER_MAX_LENGTH = 9;
export const DVA_NUMBER_MIN_LENGTH = 8;

export const DVA_CARD_TYPE_MAPPING = {
  GOLD: 1,
  WHITE: 2,
  // LILAC: 3, // NO LONGER IN USE
  ORANGE: 4,
  // BLUE: 5, // PENSIONER CONCESSION CARDS
};

export const DVA_CARD_TYPE_REVERSE_MAPPING = Object.fromEntries(
  Object.entries(DVA_CARD_TYPE_MAPPING).map(([key, value]) => [value, key])
);

const DVA_CARD_TYPE_ACCEPTED_VALUES = Object.values(DVA_CARD_TYPE_MAPPING);

/**
 * Validates DVA number.
 * TODO: reinforce validation rules in the future.
 * Remove the comments if already applied.
 * @param {string} value - The DVA number to validate.
 * @returns {boolean} True if valid, otherwise false.
 */
export const validateDvaNumber = (value) => {
  if (typeof value !== 'string') {
    return false;
  }
  
  return DVA_NUMBER_MIN_LENGTH <= value.length && value.length <= DVA_NUMBER_MAX_LENGTH;
}

/**
 * Validates the DVA card type value.
 * 
 * A value is considered valid if it is:
 * - null (indicating no selection), or
 * - an integer that is included in the list of accepted DVA card types.
 *
 * @param {*} value - The value to validate.
 * @returns {boolean} Returns true if the value is a valid DVA card type, otherwise false.
 */
export const validateDvaCardType = (value) => {
  if (!isValidInteger(value)) {
    return false;
  }

  return DVA_CARD_TYPE_ACCEPTED_VALUES.includes(value);
}
